/**
 * Free gift (e.g. a shaker) for the NEM Spar-Abo.
 *
 * - When a Spar-Abo variant is added to the cart, add one gift per Spar-Abo in the cart - unless the
 *   customer already put the gift product in the cart themselves, which then counts. Gift lines
 *   carry a hidden "_nem_abo_gift" property holding the Spar-Abo variant id, which keeps them on
 *   their own line and separate from a gift product the customer bought on purpose.
 * - Whenever the cart changes (and on page load, e.g. after a change on the cart page), keep each
 *   gift line in step with its Spar-Abo's quantity - down to 0 when the Spar-Abo is removed. A gift
 *   line the customer removed entirely is not brought back. Gifts bought on purpose are never
 *   touched, and buying one later doesn't take away a gift already in the cart.
 *
 * Whether the gift is actually free depends on a "Buy X get Y" automatic discount configured in
 * Shopify admin - this script only handles adding and removing it.
 *
 * Adding reads its config from <script type="application/json" data-nem-abo-gift-config> blocks
 * left by snippets/variant-picker-nem-kapsel.liquid. Keeping quantities in sync needs no config,
 * so this script is loaded on every page (snippets/nem-abo-gift.liquid).
 */
(function () {
  var GIFT_PROPERTY = "_nem_abo_gift";
  var isSyncing = false;

  function readConfigs() {
    return Array.from(document.querySelectorAll("[data-nem-abo-gift-config]"))
      .map(function (node) {
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          return null;
        }
      })
      .filter(function (config, index, configs) {
        // The product form and the quick buy form each leave a copy - keep one per Spar-Abo.
        return (
          config &&
          configs.findIndex(function (other) {
            return other && String(other.aboVariantId) === String(config.aboVariantId);
          }) === index
        );
      });
  }

  function quantityOfVariant(cart, variantId) {
    return cart.items.reduce(function (total, item) {
      return String(item.variant_id) === String(variantId) ? total + item.quantity : total;
    }, 0);
  }

  function giftLines(cart) {
    return cart.items.filter(function (item) {
      return item.properties && item.properties[GIFT_PROPERTY];
    });
  }

  function quantityOfGiftsFor(cart, aboVariantId) {
    return giftLines(cart).reduce(function (total, item) {
      return String(item.properties[GIFT_PROPERTY]) === String(aboVariantId) ? total + item.quantity : total;
    }, 0);
  }

  // How many gift lines each Spar-Abo should have: one per Spar-Abo, minus gift products the
  // customer put in the cart on purpose - those are made free by the discount instead of adding
  // a second one. Each bought gift only covers one Spar-Abo, also across several Spar-Abo products.
  function wantedGiftQuantities(cart, pairs) {
    var boughtGifts = {};
    var wanted = {};

    pairs.forEach(function (pair) {
      var aboVariantId = String(pair.aboVariantId);
      var giftVariantId = String(pair.giftVariantId);
      if (aboVariantId in wanted) {
        return;
      }

      if (!(giftVariantId in boughtGifts)) {
        boughtGifts[giftVariantId] = cart.items.reduce(function (total, item) {
          var isBought = String(item.variant_id) === giftVariantId && !(item.properties && item.properties[GIFT_PROPERTY]);
          return isBought ? total + item.quantity : total;
        }, 0);
      }

      var abos = quantityOfVariant(cart, aboVariantId);
      var coveredByBought = Math.min(abos, boughtGifts[giftVariantId]);
      boughtGifts[giftVariantId] -= coveredByBought;
      wanted[aboVariantId] = abos - coveredByBought;
    });

    return wanted;
  }

  // Ask for the cart drawer's re-rendered HTML in the same request (like the theme's own product
  // form does), so the drawer shows the change right away - without it only the count bubble updates.
  function sectionsToBundle() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sections },
      })
    );
    return sections;
  }

  function announceCartChange(cart, sections, baseEvent) {
    cart.sections = sections || {};
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: { baseEvent: baseEvent, cart: cart },
      })
    );
  }

  function addGifts(config, quantity) {
    var sections = sectionsToBundle();
    var properties = {};
    properties[GIFT_PROPERTY] = String(config.aboVariantId);

    return fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({
        items: [{ id: Number(config.giftVariantId), quantity: quantity, properties: properties }],
        sections: sections,
      }),
    })
      .then(function (response) {
        return response.json().then(function (json) {
          return { ok: response.ok, json: json };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          return;
        }

        return fetch(window.Shopify.routes.root + "cart.js")
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            announceCartChange(cart, result.json.sections, "variant:add");
          });
      });
  }

  // Line keys -> new quantity for gift lines that no longer fit the number of Spar-Abos they belong
  // to: never more gifts than Spar-Abos (down to 0 when a Spar-Abo was removed), and more gifts when
  // a Spar-Abo's quantity went up. A gift product bought on purpose only counts toward that increase -
  // buying an extra one later must not take away the gift that is already there.
  function giftUpdates(cart) {
    var lines = giftLines(cart);
    var wanted = wantedGiftQuantities(
      cart,
      lines.map(function (item) {
        return { aboVariantId: item.properties[GIFT_PROPERTY], giftVariantId: item.variant_id };
      })
    );
    var updates = {};
    var seenAbos = {};

    lines.forEach(function (item) {
      var aboVariantId = String(item.properties[GIFT_PROPERTY]);
      // Normally one gift line per Spar-Abo; should there be more, the first one takes the full count.
      if (seenAbos[aboVariantId]) {
        updates[item.key] = 0;
        return;
      }
      seenAbos[aboVariantId] = true;

      var current = quantityOfGiftsFor(cart, aboVariantId);
      var abos = quantityOfVariant(cart, aboVariantId);
      var target = current;
      if (current > abos) {
        target = abos;
      } else if (current < wanted[aboVariantId]) {
        target = wanted[aboVariantId];
      }

      if (target !== item.quantity) {
        updates[item.key] = target;
      }
    });

    return updates;
  }

  function syncGiftQuantities(cart) {
    var updates = giftUpdates(cart);
    if (!Object.keys(updates).length) {
      return Promise.resolve();
    }

    if (window.themeVariables && window.themeVariables.settings.pageType === "cart") {
      // The cart page renders server-side and reloads after every change, so do the same here.
      return fetch(window.Shopify.routes.root + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates: updates }),
      }).then(function () {
        window.location.reload();
      });
    }

    var sections = sectionsToBundle();
    return fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates: updates, sections: sections }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        // "line-item:change" (not "variant:add") so the drawer applies this after the theme's own
        // delayed update for the change that triggered it, instead of being overwritten by it.
        announceCartChange(cart, cart.sections, "line-item:change");
      });
  }

  function runExclusively(task) {
    if (isSyncing) {
      return;
    }
    isSyncing = true;
    task()
      .catch(function () {
        /* Network hiccup - leave the cart as it is rather than bother the customer. */
      })
      .then(function () {
        isSyncing = false;
      });
  }

  document.addEventListener("variant:add", function (event) {
    var addedItems = event.detail && event.detail.items;
    var cart = event.detail && event.detail.cart;
    if (!addedItems || !addedItems.length || !cart) {
      return;
    }

    var configs = readConfigs().filter(function (config) {
      return addedItems.some(function (item) {
        return String(item.variant_id || item.id) === String(config.aboVariantId);
      });
    });

    if (!configs.length) {
      return;
    }

    var wanted = wantedGiftQuantities(cart, configs);

    runExclusively(function () {
      return configs.reduce(function (previous, config) {
        return previous.then(function () {
          var missing = wanted[String(config.aboVariantId)] - quantityOfGiftsFor(cart, config.aboVariantId);
          return missing > 0 ? addGifts(config, missing) : undefined;
        });
      }, Promise.resolve());
    });
  });

  document.addEventListener("cart:change", function (event) {
    var cart = event.detail && event.detail.cart;
    // Nothing to fix (the usual case): stay out of the way, so a Spar-Abo added right after isn't blocked.
    if (!cart || !cart.items || isSyncing || !Object.keys(giftUpdates(cart)).length) {
      return;
    }

    runExclusively(function () {
      return syncGiftQuantities(cart);
    });
  });

  // Catch changes that happened without an event on this page (e.g. on the cart page, which reloads).
  if (document.querySelector("[data-nem-abo-gift-in-cart]")) {
    runExclusively(function () {
      return fetch(window.Shopify.routes.root + "cart.js")
        .then(function (response) {
          return response.json();
        })
        .then(syncGiftQuantities);
    });
  }
})();
